import type { IProductBundleOption } from "@rogue/universal"
import { BundleUIOption } from "../RuleTypes"
import { SharedRuleContext, applySharedRules } from "../shared-rules"
import { shouldHideIndyTrolleyAttachment } from "./indy-rules"
import { IRogue, Product, BuyRequest } from "@/js/services/RogueTypes"
import { findBundleProduct } from "@/js/pages/product/rackbuilder/utils/rackbuilder-util"
import { getSimpleForBuyRequest } from "@/js/helpers/buy-request"

export function getOptionsForRackBundle(rogue: IRogue, rack: Product, bundleOption: IProductBundleOption, options: BundleUIOption[], bundleBuyRequest: BuyRequest, isSlingerX = false) {
  const title = bundleOption.title.toLowerCase().replace(':', '').trim()
  const rackHeight = rack.attributes.rack_height
  const isOptFor6Post = ['front pull-up bar', 'safeties'].includes(title)
  const isOptFor4Post = ['add half rack feet'].includes(title)
  const isIndy = rack.name.includes('INDY')
  
  let retval = options

  // If this is a 4 post rack, hide the Safeties and Front Pull-Up Bar
  if (isOptFor6Post || isOptFor4Post) {
    const rackSize = rack.attributes.rack_size?.toString().toLowerCase()
    const is4Post = Boolean(rackSize && rackSize.indexOf('4') !== -1)
    const is6Post = Boolean(rackSize && rackSize.indexOf('6') !== -1)

    if (is4Post || is6Post) {
      // If this is a 6 post option on a 4 post FTX, hide it completely
      if ((is4Post && isOptFor6Post) || (is6Post && isOptFor4Post)) {
        if (isIndy && is4Post && title.includes('front pull-up') && rackHeight === '80"') {
          // Instead of no options, the indy 4 post will have 1 option for front pull-up bar XM-438 Monster 43
          return options.filter(op => op.label.includes('XM-438'))
        }

        // Slinger X requires the front pull-up bar / crossmember on every post count (unlike other FTX racks) - don't hide it on 4 post.
        // Safeties stay hidden on 4 post Slinger X - that space is occupied by the weight stack.
        const isSlingerXFrontPullUp = isSlingerX && is4Post && title.includes('front pull-up')

        if (!isSlingerXFrontPullUp && (!isIndy || (isIndy && !title.includes('front pull-up')))) {
          /*
            To prevent breaking the rhino, return empty array if it's not the indy
            If it is the indy, return empty options if it's not the front pull up bar because the 90 and 100 should have those options
          */
          return []
        }
      }

      if (is6Post && isOptFor6Post && title !== 'safeties') {
        // If this is a 6 post rack, and the option is a 6 post option, we need to force them to select one.
        // So we filter out the None option.
        retval = retval.filter(opt => opt.label !== 'None')
      }
    }
  }

  // Figure out what depth / height the rack has. We need to match other bundle options to it.
  const height = rack.attributes.rack_height
  const depth = rack.attributes.rack_depth
  const rackBeamColor = rack.attributes.beam_color

  // Make sure this product matches the rack configuration
  return retval.filter(opt => {
    if (!opt.value) {
      return true
    }

    const product = rogue.getProductById(opt.value)

    const selectedHardware = findBundleProduct(rogue, bundleBuyRequest, 'hardware')?.name as string
    const isIndyHR = isIndy && rack?.attributes?.rack_size === '4'

    let sanitizedHardwareName = 'Bright Zinc'
    if (selectedHardware?.includes('Proprietary') || isIndy) {
      sanitizedHardwareName = 'Matte Black'
    } else if (selectedHardware?.includes('Black') && selectedHardware?.includes('Zinc')) {
      sanitizedHardwareName = 'Black Zinc'
    }

    const sanitizedStabilizerName = product.name.includes('Stabilizer') ? product.name.replace(/^.*\//, '') : product.name

    const mismatchedStabilizerSelection = product.name.includes('Stabilizer') && !sanitizedStabilizerName?.includes(sanitizedHardwareName)

    const isSatinBlackRearCrossmember = product.name.includes('Beam w/ Gusset') && product.name.includes('Satin Black') && !product.name.includes('Crown')

    const isPinPipeSafety = product?.name?.includes('Pin') && product?.name?.includes('Pipe')
    const isPlateStorage = product?.name?.includes('Storage') && product?.name?.includes('Pin')
    const productHasMismatchedHardware = sanitizedHardwareName !== product.attributes?.hardware_finish

    const isSingleTrolleyAttachment = product.name.toLowerCase()?.includes('single handle cable attachment')

    let isRhinoRackTrainer
    let isRhinoIndyTrainer
    let isRhinoFXT

    // The first index is always the rack config and the second is always the FXT type (e.g. Rhino, Indy, Rhino FXT)
    if (bundleBuyRequest.children?.[1]) {
      isRhinoRackTrainer = getSimpleForBuyRequest(bundleBuyRequest.children[1], rogue).attributes.rig_type === 'Rhino Rack Trainer'
      isRhinoFXT = getSimpleForBuyRequest(bundleBuyRequest.children[1], rogue).attributes.rig_type === 'Rhino Functional Trainer'
      isRhinoIndyTrainer = getSimpleForBuyRequest(bundleBuyRequest.children[1], rogue).attributes.rig_type === 'Rhino + INDY'
    }

    const context: SharedRuleContext = {
      rogue,
      rack,
      product,
      rackBeamColor,
      sanitizedHardwareName,
    }

    // Hide trolley attachments for Indy & Rhino+INDY Trainers
    if ((isIndy || isRhinoIndyTrainer)) {
      const indySideAttribute = String(findBundleProduct(rogue, bundleBuyRequest, 'trainer')?.attributes?.side)

      if (shouldHideIndyTrolleyAttachment(isSingleTrolleyAttachment, indySideAttribute, product.name)) return false
    }

    // Hide the trolley attachment on the Rhino Rack Trainer
    if (isRhinoRackTrainer && isSingleTrolleyAttachment) {
      return false
    }

    // Hide the non dual single trolley attachment on the Rhino FXT
    if (isRhinoFXT && isSingleTrolleyAttachment && !String(product.name).toLowerCase()?.includes('(pair)')) {
      return false
    }

    // Hide band pegs on the Indy HR
    if (isIndyHR && product.name.includes('Band Peg')) {
      return false
    }

    // Show the satin black rear crossmember only with mg colors (must run before applySharedRules to prevent ruleBeamColor from hiding it)
    if (isSatinBlackRearCrossmember && !rackBeamColor?.toString().includes('Textured')) {
      return true
    }

    // Apply shared rules (offset bar, 80" bars, crowns, beam color, nameplates, etc.)
    const sharedResult = applySharedRules(context, bundleOption.title)
    if (sharedResult !== null) return sharedResult

    // Hide the pin/pipe option that does not match the hardware option on RML racks
    if (isPinPipeSafety && productHasMismatchedHardware && rack.name?.includes('RML')) {
      return false
    }

    // Hide the plate storage that does not match the hardware option on RML racks
    if (isPlateStorage && productHasMismatchedHardware && rack.name?.includes('RML')) {
      return false
    }

    // Only show stabilizer with the same hardware as the rack
    if (mismatchedStabilizerSelection) {
      return false
    }

    // If we don't know about the product, this is an invalid bundle option
    if (!product) {
      return false
    }

    // Configs are never hidden. Maybe this could check every child to see if any of them are a possibility?
    if (product.type !== 'simple') {
      return true
    }

    if (height && product.attributes.rack_height && product.attributes.rack_height !== height) {
      return false
    }

    if (depth && product.attributes.rack_depth && product.attributes.rack_depth !== depth) {
      return false
    }

    return true
  })
}